import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import type { CostCurvesStrings } from "@/components/tools/cost-curves-calculator";
import {
  EconChart,
  type EconCurve,
  type EconDot,
} from "@/components/tools/econ-micro/curve-chart";
import { t } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type CostRow,
  type CostTable,
  minimumBy,
} from "@/lib/tool-kit/econ-micro/costs";
import type { XY } from "@/lib/tool-kit/econ-micro/geometry";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const MC = CHART_COLORS[3];
const ATC = CHART_COLORS[0];
const AVC = CHART_COLORS[2];
const AFC = CHART_COLORS[1];

function seriesOf(
  rows: readonly CostRow[],
  pick: (row: CostRow) => number | null
): XY[] {
  return rows.flatMap((row) => {
    const value = pick(row);
    return value === null ? [] : [[row.q, value] as XY];
  });
}

export function CostGraph({
  table,
  price,
  showAfc,
  strings,
  fmt,
}: {
  table: CostTable;
  price: number | null;
  showAfc: boolean;
  strings: CostCurvesStrings;
  fmt: Fmt;
}) {
  const { rows } = table;
  const atc = seriesOf(rows, (r) => r.atc);
  const avc = seriesOf(rows, (r) => r.avc);
  const afc = seriesOf(rows, (r) => r.afc);
  // MC sits halfway between the two outputs it is measured over.
  const mc = rows.flatMap((row) =>
    row.mc === null || row.mcFrom === null
      ? []
      : [[(row.mcFrom + row.q) / 2, row.mc] as XY]
  );
  const xMax = Math.max(...rows.map((r) => r.q));
  const minAtc = minimumBy(rows, (r) => r.atc);
  const minAvc = minimumBy(rows, (r) => r.avc);
  const everything = [...atc, ...avc, ...mc, ...(showAfc ? afc : [])].map(
    (point) => point[1]
  );
  const floor = minAtc?.atc ?? Math.min(...everything);
  // A tall first ATC (or a steep tail of MC) would flatten the part that matters.
  let yMax = Math.min(Math.max(...everything), 3 * floor);
  yMax = Math.max(yMax, 1.3 * floor, price === null ? 0 : 1.15 * price);

  const curves: EconCurve[] = [
    { id: "mc", points: mc, color: MC, label: strings.curveMc },
    { id: "atc", points: atc, color: ATC, label: strings.curveAtc },
    { id: "avc", points: avc, color: AVC, label: strings.curveAvc },
  ];
  if (showAfc) {
    curves.push({
      id: "afc",
      points: afc,
      color: AFC,
      label: strings.curveAfc,
    });
  }
  if (price !== null) {
    curves.push({
      id: "price",
      points: [
        [0, price],
        [xMax, price],
      ],
      color: CHART_INK.text,
      dashed: true,
      label: strings.curvePrice,
    });
  }
  const dots: EconDot[] = [];
  if (minAtc && minAtc.atc !== null) {
    dots.push({
      id: "breakeven",
      x: minAtc.q,
      y: minAtc.atc,
      label: strings.pointBreakEven,
      position: "n",
      guides: true,
    });
  }
  if (minAvc && minAvc.avc !== null) {
    dots.push({
      id: "shutdown",
      x: minAvc.q,
      y: minAvc.avc,
      label: strings.pointShutdown,
      position: "s",
      guides: true,
    });
  }
  const text = (v: number) => fmt(v, { maximumFractionDigits: 2 });
  const head = [
    strings.columnQ,
    strings.curveMc,
    strings.curveAtc,
    strings.curveAvc,
    strings.curveAfc,
  ];
  const tableRows = rows
    .filter((r) => r.q > 0)
    .map((r) => [
      text(r.q),
      r.mc === null ? "—" : text(r.mc),
      text(r.atc ?? 0),
      text(r.avc ?? 0),
      text(r.afc ?? 0),
    ]);
  const title = strings.graphTitle;
  return (
    <div className="space-y-2">
      <ChartFigure
        apa={false}
        data={toTsv([
          head,
          ...rows
            .filter((r) => r.q > 0)
            .map((r) => [r.q, r.mc, r.atc, r.avc, r.afc]),
        ])}
        figureNumber="1"
        filename={chartFilename(title, "cost-curves")}
        labels={strings}
        note=""
        table={{ head, rows: tableRows }}
        title={title}
      >
        <EconChart
          curves={curves}
          description={t(strings.graphDescription, {
            atc: text(minAtc?.atc ?? 0),
            atcQ: text(minAtc?.q ?? 0),
            avc: text(minAvc?.avc ?? 0),
            avcQ: text(minAvc?.q ?? 0),
          })}
          dots={dots}
          fmt={fmt}
          id="cc-chart"
          label={title}
          xMax={xMax}
          xTitle={strings.xAxis}
          yMax={yMax}
          yTitle={strings.yAxis}
        />
      </ChartFigure>
      <p className="text-muted-foreground text-xs">{strings.graphNote}</p>
    </div>
  );
}
